import { Controller } from "@hotwired/stimulus"

// Preserves scroll position across Turbo visits and frame navigations
// (e.g. filter, sort, or tab changes in reports).
//
// The app scrolls inside a layout container (e.g. .main-wrapper), not
// window. This controller walks up from its element to find the nearest
// scrollable ancestor and saves/restores its scrollTop via sessionStorage.
//
// Attach to a wrapper element: data-controller="scroll-preserve"
export default class extends Controller {
  connect() {
    this.boundSave = this.#saveScroll.bind(this)
    this.boundRestore = this.#restoreScroll.bind(this)

    document.addEventListener("turbo:before-visit", this.boundSave)
    document.addEventListener("turbo:before-fetch-request", this.boundSave)
    document.addEventListener("turbo:load", this.boundRestore)
    document.addEventListener("turbo:frame-load", this.boundRestore)

    // Eager restore: if turbo:load already fired before this controller connected
    this.#restoreScroll()
  }

  disconnect() {
    document.removeEventListener("turbo:before-visit", this.boundSave)
    document.removeEventListener("turbo:before-fetch-request", this.boundSave)
    document.removeEventListener("turbo:load", this.boundRestore)
    document.removeEventListener("turbo:frame-load", this.boundRestore)
  }

  #saveScroll() {
    const container = this.#scrollContainer
    if (container) {
      sessionStorage.setItem(this.#storageKey, container.scrollTop.toString())
    }
  }

  #restoreScroll() {
    const saved = sessionStorage.getItem(this.#storageKey)
    if (saved != null) {
      sessionStorage.removeItem(this.#storageKey)
      this.#restoreWithRetry(parseInt(saved, 10), 10)
    }
  }

  #restoreWithRetry(value, retries) {
    const container = this.#scrollContainer
    if (!container) return

    container.scrollTop = value
    if (retries > 0 && Math.abs(container.scrollTop - value) > 10) {
      requestAnimationFrame(() => this.#restoreWithRetry(value, retries - 1))
    }
  }

  // Walk up from this element to find the nearest scrollable ancestor
  get #scrollContainer() {
    let el = this.element.parentElement
    while (el) {
      const { overflow, overflowY } = getComputedStyle(el)
      if (overflow === "auto" || overflow === "scroll" ||
          overflowY === "auto" || overflowY === "scroll") {
        return el
      }
      el = el.parentElement
    }
    return document.documentElement
  }

  get #storageKey() {
    return `scroll-position-${window.location.pathname}`
  }
}
